import React, { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { useTheme, useBreakpointValue } from 'native-base';
import { Avatar, Box, Circle, Pressable, HStack, VStack, Text, View } from 'native-base';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { addInlineStyleToMentionBlot, displayTime, getCategoryNames, getSourceHTML, isArrayValid, sentenceJoinUserNames } from '../utils/helpers';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { DEVICE_WIDTH, TYPE_MENTION, TYPE_EDITED, TYPE_LIKE, TYPE_REPLY, TYPE_POST, AVATAR_SIZE, NOTIFICATIONS_TABS, NOTIFICATION_TYPE_ICONS } from '../Constants';
import { useAuthState, authUtil } from '../../context/auth';
import { useEnvState } from '../../context/env';
import { useCategoriesState } from '../../context/categories';
import { topicsAPI, useTopicsDispatch, useTopicsState } from '../../context/topics';
import { overviewAPI, useOverviewDispatch, useOverviewState } from '../../context/overview';
import { userAPI, useUserDispatch, useUserState } from '../../context/user';
import { useMessagesDispatch } from '../../context/messages';
import RenderHtml from 'react-native-render-html';
import { useDomParser } from '../hooks/useDomParser';
import ContextMenu from './ContextMenu';
import { useMessageBus } from '../hooks/useMessageBus';
const Notification = ({
  notification,
  navigation,
  setIsPopoverOpen,
  selected,
  loadContents
}) => {
  const theme = useTheme(),
    {
      colors
    } = theme;
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [notificationTypeText, setNotificationTypeText] = useState('');
  const [userPresent, setUserPresent] = useState(false);
  const authState = useAuthState();
  const envState = useEnvState();
  const topicsState = useTopicsState();
  const topicsDispatch = useTopicsDispatch();
  const userState = useUserState();
  const userDispatch = useUserDispatch();
  const messagesDispatch = useMessagesDispatch();
  const overviewState = useOverviewState();
  const overviewDispatch = useOverviewDispatch();
  const {
    getParser,
    normalizeImages,
    displayAtMentions
  } = useDomParser();
  const {
    closeReplyBanner
  } = useMessageBus({
    userAuthenticated: authUtil.userAuthed(authState)
  });
  const {
    id,
    read,
    unseen,
    notification_type,
    created_at,
    last_posted_at,
    topic_id,
    title,
    fancy_title,
    posters = [],
    participants = [],
    allowed_user_summary = [],
    last_poster_username,
    data
  } = notification;

  /* Definition of posters and participants:
   * Posters: All folks who have actually posted in the topic in question
   *       for private messages, we shouldn't allow more than 2
   * Participants: All users who have access to the thread BESIDES the current user (self)
   *       for private messages, just one person
   */

  const categoriesState = useCategoriesState();
  const {
    forumCategories: categories,
    rootProductCategory
  } = categoriesState;
  const categoryIds = categories.map(category => category.id);
  const categoryExists = (data === null || data === void 0 ? void 0 : data.category_id) && categoryIds.includes(data.category_id);
  const categoriesInfo = categoryExists ? getCategoryNames(data.category_id, rootProductCategory, categories) : '';
  const categoryName = isArrayValid(categoriesInfo) ? categoriesInfo[categoriesInfo.length - 1] : '';
  const notification_viewed = read;
  const {
    users
  } = userState;
  // allowed_user_summary is only defined for PMs
  const uniquePMusers = allowed_user_summary.map(u => {
    let userInfo = [...posters, ...participants].find(p => p.username === u);
    if (userInfo) {
      let userContextEntry = users.find(u => u.username === userInfo.username);
      return userContextEntry;
    }
    return userInfo;
  }).filter(u => u);
  useEffect(() => {
    if (!userPresent && selected === NOTIFICATIONS_TABS[1].name) {
      populateUserArray();
    }
  }, []);
  const populateUserArray = async () => {
    let usersPresent = allowed_user_summary.map(au => {
      const userContextEntry = users.find(u => u.username === au);
      return {
        username: au,
        present: !!userContextEntry
      };
    });
    if (!usersPresent.every(u => u.present)) {
      // let's go find the user and add them to this list
      let newUsers = await Promise.all(usersPresent.map(u => {
        if (u.present) return null;
        return userAPI.getUser(envState.base_url, u.username, authState.api_key);
      }));
      newUsers.filter(u => u).forEach(u => {
        userDispatch({
          type: 'ADD_USER',
          payload: u.user
        });
      });
      setUserPresent(true);
    }
    return true;
  };
  const getParticipantData = () => {
    const participantsUsernames = uniquePMusers === null || uniquePMusers === void 0 ? void 0 : uniquePMusers.map(participant => participant.username).filter(i => i !== authState.username);
    let otherParticipantData = users.filter(user => participantsUsernames.find(pu => pu === user.username));
    return otherParticipantData;
  };
  const getAvatarTemplate = () => {
    let avatar_template;
    if (selected === NOTIFICATIONS_TABS[0].name) {
      avatar_template = data === null || data === void 0 ? void 0 : data.avatar_template;
    } else if (selected === NOTIFICATIONS_TABS[1].name) {
      let user = getParticipantData().find(u => u.username === last_poster_username);
      avatar_template = user === null || user === void 0 ? void 0 : user.avatar_template;
    }
    return avatar_template;
  };
  const avatar_template = getAvatarTemplate();
  const avatarTemplate = avatar_template ? avatar_template.replace(/\{size\}/, AVATAR_SIZE) : null;
  useEffect(() => {
    displayNotificationTypeText();
  }, [notification_type]);
  const excerptWithPTag = data && `<p>${data.excerpt}</p>`; // excerpt initially has no html tags such as <p>
  let sourceHTML = getSourceHTML(excerptWithPTag, colors);

  // parse/normalize markup
  const parser = getParser();
  if (parser) {
    sourceHTML = normalizeImages(parser, sourceHTML, DEVICE_WIDTH);
    sourceHTML = displayAtMentions(parser, sourceHTML);
    sourceHTML = addInlineStyleToMentionBlot(parser, sourceHTML, colors);
  }
  const updateNotifications = activityId => {
    if (selected === NOTIFICATIONS_TABS[0].name) {
      overviewDispatch({
        type: 'SET_NOTIFICATIONS',
        payload: {
          activity: overviewState.notifications.activity.filter(item => item.id !== activityId)
        }
      });
      overviewDispatch({
        type: 'SET_UNREAD_ACTIVITY_COUNT',
        payload: overviewState.unreadActivityCount - 1
      });
    } else if (selected === NOTIFICATIONS_TABS[1].name) {
      // overwrite the messages to be read
      overviewDispatch({
        type: 'SET_NOTIFICATIONS',
        payload: {
          message: overviewState.notifications.message.map(item => {
            return {
              ...item,
              read: item.topic_id === topic_id ? true : item.read
            };
          })
        }
      });
      overviewDispatch({
        type: 'SET_MESSAGES',
        payload: overviewState.messages
      }); //reset messages with read notification
      overviewDispatch({
        type: 'SET_UNREAD_MESSAGES_COUNT',
        payload: overviewState.unreadMessagesCount - 1
      });
      closeReplyBanner(topic_id);
    }
  };

  // following few functions only used for an activity-type notification, not message
  const displayNotificationTypeText = () => {
    switch (notification_type) {
      case TYPE_MENTION:
        setNotificationTypeText('mentioned you');
        break;
      case TYPE_EDITED:
        setNotificationTypeText('edited post');
        break;
      case TYPE_LIKE:
        setNotificationTypeText('liked your post');
        break;
      case TYPE_REPLY:
        setNotificationTypeText('wrote a reply');
        break;
      case TYPE_POST:
        setNotificationTypeText('wrote a post');
        break;
      default:
        setNotificationTypeText('');
    }
  };
  const setIconByType = () => {
    const icon = NOTIFICATION_TYPE_ICONS.find(icon => icon.type === notification_type);
    if (icon) {
      return icon.icon;
    }
  };
  const notificationIcon = setIconByType();
  const descriptiveIcon = /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: notificationIcon,
    color: colors.activity.text
  });
  const descriptiveText = /*#__PURE__*/React.createElement(Text, {
    fontSize: "xs",
    color: colors.activity.notificationTypeText
  }, notificationTypeText);
  const description = useBreakpointValue({
    base: descriptiveIcon,
    '3xs': descriptiveIcon,
    '2xs': descriptiveText,
    sm: descriptiveText // nothing popped up from "sm" and above until I added this
  });
  // end activity-type notification description

  const handleOnPress = async () => {
    if (!notification.read) {
      // update to read if !read
      const response = await overviewAPI.setActivityToRead(envState.base_url, authState.api_key, id);
      if (!response) {
        throw new Error("Couldn't mark notification as read. Please try again");
      } else {
        updateNotifications && updateNotifications(id);
        loadContents ? loadContents() : console.log(''); // state persists when pressing Go back button
      }
    }
    setIsPopoverOpen && setIsPopoverOpen(false);
    if (selected === NOTIFICATIONS_TABS[0].name) {
      const originalReply = data ? await topicsAPI.getReply(envState.base_url, authState.api_key, data.original_post_id) : 0;
      if (originalReply) {
        navigation.navigate('SingleTopic', {
          topicId: topic_id,
          normalizeUrl: true,
          replyId: data.original_post_id,
          reply: originalReply
        });
      }
    } else if (selected === NOTIFICATIONS_TABS[1].name) {
      const participantsData = getParticipantData();
      messagesDispatch({
        type: 'SET_MESSAGE_USERS',
        payload: participantsData
      });
      navigation.navigate('PrivateMessage', {
        topicId: topic_id
      });
    }
  };
  return /*#__PURE__*/React.createElement(Pressable, {
    onPress: handleOnPress,
    borderRadius: 5,
    w: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    p: "4",
    bg: notification_viewed ? colors.activity.read : colors.activity.unread,
    mx: "2",
    my: "1",
    borderRadius: 5
  }, /*#__PURE__*/React.createElement(VStack, {
    space: 3
  }, /*#__PURE__*/React.createElement(HStack, {
    space: 2,
    alignItems: 'center',
    w: "100%"
  }, avatarTemplate && /*#__PURE__*/React.createElement(Avatar, {
    source: {
      uri: `${envState.base_url}${avatarTemplate}`
    },
    size: 'sm',
    mx: 2,
    mt: 2
  }), /*#__PURE__*/React.createElement(VStack, null, /*#__PURE__*/React.createElement(HStack, {
    alignItems: "center",
    space: 1
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 'sm',
    fontWeight: 'semibold'
  }, selected === NOTIFICATIONS_TABS[0].name ? data.notifying_user_name ? data.notifying_user_name + '.' : data.display_username : sentenceJoinUserNames({
    authState,
    users: (uniquePMusers === null || uniquePMusers === void 0 ? void 0 : uniquePMusers.filter(u => u.username !== authState.username)) || [],
    includeSelf: false
  })), selected === NOTIFICATIONS_TABS[0].name && /*#__PURE__*/React.createElement(View, null, description)), /*#__PURE__*/React.createElement(HStack, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 'xs',
    color: colors.activity.timestamp
  }, displayTime(selected === NOTIFICATIONS_TABS[0].name ? created_at : last_posted_at), " ago"), categoryExists ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 'xs',
    color: colors.activity.timestamp
  }, ' ', "in", ' '), /*#__PURE__*/React.createElement(Text, {
    fontSize: 'xs',
    color: colors.activity.link
  }, categoryName)) : '')), /*#__PURE__*/React.createElement(HStack, {
    alignItems: "center",
    space: 4,
    position: "absolute",
    top: "0",
    right: "0"
  }, selected === NOTIFICATIONS_TABS[0].name && /*#__PURE__*/React.createElement(ContextMenu, {
    id: id,
    read: notification_viewed,
    selected: selected,
    updateNotifications: updateNotifications,
    isContextMenuOpen: isContextMenuOpen,
    setIsContextMenuOpen: setIsContextMenuOpen
  }), !notification_viewed && /*#__PURE__*/React.createElement(Circle, {
    size: '12px',
    bg: colors.activity.link
  }))), data !== null && data !== void 0 && data.excerpt ? /*#__PURE__*/React.createElement(RenderHtml, {
    defaultTextProps: {
      selectable: true
    },
    contentWidth: Platform.OS === 'web' ? null : DEVICE_WIDTH,
    source: {
      html: sourceHTML
    }
  }) : /*#__PURE__*/React.createElement(Text, {
    fontSize: 'sm',
    color: colors.activity.link
  }, fancy_title), /*#__PURE__*/React.createElement(HStack, {
    alignItems: 'center',
    space: 1
  }, /*#__PURE__*/React.createElement(Text, {
    color: colors.activity.link
  }, "Go to post"), /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faChevronRight,
    color: colors.activity.link
  })))));
};
export default Notification;
//# sourceMappingURL=Notification.js.map